import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { isEqual } from "lodash";
import styles from "./Components.module.scss";

export interface SchemaFormShownActionButtons {
    hasRemoveButton: boolean;
    hasResetToDefaultButton: boolean;
    hasClearButton: boolean;
}

interface SchemaFormActionProps {
    value: any;
    defaultValue?: any;
    elementType?: string;
    unsetField: () => void;
    resetToDefault: () => void;
    isRequired?: boolean;
    removeField?: () => void;
    isOptional: boolean;
    isSetToDefaultAllowed: boolean;
    shownButtons: SchemaFormShownActionButtons;
}

interface SchemaFormSingleAction {
    value: any;
    onClick: () => void;
    hasButton: boolean;
}

export const unsetValue = null;

const ResetToDefaultButton = ({
    value,
    isSetToDefaultAllowed,
    defaultValue,
    elementType,
    onClick,
    hasButton,
}: SchemaFormSingleAction & {
    defaultValue?: any;
    elementType?: string;
    isSetToDefaultAllowed?: boolean;
}) => {
    const isObjectType = elementType === "form-object" || elementType === "form-optional-object";
    const isArrayType = elementType === "form-array" || elementType === "form-optional-array";
    const hasDefault = defaultValue !== undefined;
    const isDefaultApplied = isEqual(defaultValue, value);
    const objectOverlay = isDefaultApplied
        ? "default properties are applied"
        : "reset all properties of the object to default";
    const arrayOverlay = isDefaultApplied
        ? "default items are applied"
        : "reset items to default items";
    const elementsOverlay = isDefaultApplied
        ? "default is applied"
        : `reset to default ${defaultValue === "" ? `""` : defaultValue}`;
    const overlay = hasDefault
        ? isObjectType
            ? objectOverlay
            : isArrayType
            ? arrayOverlay
            : elementsOverlay
        : "cannot reset to default";

    return isSetToDefaultAllowed && hasButton ? (
        <Tooltip overlay={overlay}>
            <span className={styles.schemaFormActionButton}>
                <Button
                    icon={IconCollection.refresh}
                    onClick={onClick}
                    disabled={!hasDefault || isDefaultApplied}
                    variation={"white"}
                />
            </span>
        </Tooltip>
    ) : null;
};

const UnsetButton = ({
    value,
    isOptional,
    onClick,
    hasButton,
}: SchemaFormSingleAction & { isOptional?: boolean }) => {
    const isCleared = value === unsetValue;
    const overlay = isCleared ? "field already cleared" : "clear field";

    return isOptional && hasButton ? (
        <Tooltip overlay={overlay}>
            <span className={styles.schemaFormActionButton}>
                <Button
                    icon={IconCollection.close}
                    onClick={onClick}
                    disabled={isCleared}
                    variation={"white"}
                />
            </span>
        </Tooltip>
    ) : null;
};

export const SchemaFormActionButtons = ({
    value,
    defaultValue,
    elementType,
    isRequired,
    unsetField,
    resetToDefault,
    removeField,
    isOptional,
    isSetToDefaultAllowed,
    shownButtons,
}: SchemaFormActionProps) => {
    const isObjectType = elementType === "form-object" || elementType === "form-optional-object";
    return (
        <span
            className={`${styles.schemaFormActions} ${isObjectType ? "object-action-buttons" : ""}`}
        >
            <ResetToDefaultButton
                value={value}
                isSetToDefaultAllowed={isSetToDefaultAllowed}
                defaultValue={defaultValue}
                elementType={elementType}
                onClick={resetToDefault}
                hasButton={shownButtons.hasResetToDefaultButton}
            />
            <UnsetButton
                value={value}
                isOptional={isOptional}
                onClick={unsetField}
                hasButton={shownButtons.hasClearButton}
            />
            <RemoveButton
                value={value}
                isRequired={isRequired}
                onClick={removeField}
                hasButton={shownButtons.hasRemoveButton}
            />
        </span>
    );
};

const RemoveButton = ({
    onClick,
    isRequired,
    value,
    hasButton,
}: {
    onClick?: () => void;
    isRequired?: boolean;
    value?: any;
    hasButton: boolean;
}) =>
    isRequired === false && value !== undefined && onClick && hasButton ? (
        <Tooltip overlay="remove">
            <span className={styles.schemaFormActionButton}>
                <Button icon={IconCollection.deleteOutline} onClick={onClick} variation={"white"} />
            </span>
        </Tooltip>
    ) : null;
